<template>
  <div :style="rehabMerchantThemeStyle">
      <div class="rehab-title">
        <span>{{ myTitle }}</span>
        <van-icon 
          v-if="isLoggedIn" 
          name="arrow-down" 
          class="rehab-title__refresh" 
          @click="manualRefresh"
          :class="{ 'rehab-title__refresh--loading': refreshing || loading }"
        />
      </div>
      
      <!-- 未登录状态 -->
      <div v-if="!isLoggedIn" class="rehab-my__login-guide">
        <div class="rehab-my__login-card">
          <div class="rehab-my__login-icon">
            <van-icon name="user-circle-o" size="48" color="var(--rehab-theme-color, #07c160)" />
          </div>
          <div class="rehab-my__login-title">尚未登录</div>
          <div class="rehab-my__login-desc">登录后可查看您的学习记录和收藏的课程</div>
          <div class="rehab-my__login-actions">
            <van-button 
              type="primary" 
              color="var(--rehab-theme-color, #07c160)" 
              round 
              block 
              @click="goToLogin"
            >
              立即登录
            </van-button>
          </div>
        </div>
      </div>
      
      <!-- 已登录状态 -->
      <div v-else>
        <div class="rehab-my__search">
          <van-search placeholder="搜索我的课程" shape="round" v-model="keyword" @search="onSearch" />
        </div>
        
        <!-- 下拉刷新 -->
        <van-pull-refresh 
          v-model="refreshing" 
          @refresh="onRefresh"
          :disabled="loading"
          pulling-text="下拉即可刷新..."
          loosing-text="释放即可刷新..."
          loading-text="正在刷新..."
          success-text="刷新成功"
        >
          <div class="rehab-my">
            <!-- 加载状态 -->
            <div v-if="loading && !refreshing" class="rehab-my__loading">
              <van-loading color="var(--rehab-theme-color, #07c160)" text-color="var(--rehab-theme-color, #07c160)">
                正在加载课程...
              </van-loading>
            </div>
            
            <!-- 空状态 -->
            <div v-else-if="!filteredCourses.length" class="rehab-my__empty">
              <div v-if="!keyword" class="rehab-my__empty-container">
                <!-- 空状态图标 -->
                <div class="rehab-my__empty-icon">
                  <van-icon name="video-o" size="48" color="#d4d8de" />
                </div>
                
                <!-- 主标题 -->
                <div class="rehab-my__empty-title">还没有学习记录</div>
                
                <!-- 激励文案 -->
                <div class="rehab-my__empty-desc">
                  <p>开启您的康复之旅，每一步都是健康的进步</p>
                  <p>专业康复课程助您重塑健康生活</p>
                </div>
                
                <!-- 引导按钮 -->
                <div class="rehab-my__empty-actions">
                  <van-button 
                    type="primary" 
                    color="linear-gradient(135deg, var(--rehab-theme-color, #07c160) 0%, var(--rehab-theme-color-dark, #06a84e) 100%)" 
                    round 
                    size="normal"
                    @click="goToCourseCenter"
                    icon="home-o"
                  >
                    前往课程中心选择课程
                  </van-button>
                </div>
                
                <!-- 贴心提示 -->
                <div class="rehab-my__empty-tips">
                  <div class="rehab-my__tip-item">
                    <van-icon name="star-o" size="14" color="var(--rehab-theme-color, #07c160)" />
                    <span>海量专业康复课程</span>
                  </div>
                  <div class="rehab-my__tip-item">
                    <van-icon name="play-circle-o" size="14" color="var(--rehab-theme-color, #07c160)" />
                    <span>随时随地在线学习</span>
                  </div>
                  <div class="rehab-my__tip-item">
                    <van-icon name="like-o" size="14" color="var(--rehab-theme-color, #07c160)" />
                    <span>科学有效的康复指导</span>
                  </div>
                </div>
                
                <!-- 刷新提示 -->
                <div class="rehab-my__empty-refresh">下拉可以刷新数据</div>
              </div>
              
              <!-- 搜索无结果状态 -->
              <div v-else class="rehab-my__empty-search">
                <div class="rehab-my__empty-icon">
                  <van-icon name="search" size="36" color="#d4d8de" />
                </div>
                <div class="rehab-my__empty-title">暂无匹配课程</div>
                <div class="rehab-my__empty-desc">试试其他关键词或查看全部课程</div>
                <div class="rehab-my__empty-actions">
                  <van-button 
                    size="small" 
                    round 
                    @click="clearSearch"
                  >
                    清空搜索
                  </van-button>
                  <van-button 
                    type="primary" 
                    color="var(--rehab-theme-color, #07c160)" 
                    size="small" 
                    round 
                    @click="goToCourseCenter"
                  >
                    浏览课程
                  </van-button>
                </div>
              </div>
            </div>

            <!-- 课程列表 -->
            <div v-else class="rehab-my__list">
              <div class="rehab-my__card" v-for="item in filteredCourses" :key="item.id" @click="goDetail(item)">
                <div class="rehab-my__cover" :style="{ backgroundImage: 'url(' + item.cover + ')' }">
                  <div class="rehab-my__tag rehab-my__tag--vip" v-if="item.vipOnly && !item.isFree">会员</div>
                  <div class="rehab-my__tag rehab-my__tag--free" v-else-if="item.isFree">免费</div>
                </div>
                <div class="rehab-my__info">
                  <div class="rehab-my__title">{{ item.title }}</div>
                  <div class="rehab-my__meta">分类：{{ item.category }} </div>
                  <div class="rehab-my__watch-time">最后播放：{{ formatWatchTime(item.lastWatchTime) }}</div>
                  <van-progress :percentage="item.progress" pivot-text="" stroke-width="6" color="var(--rehab-theme-color, #07c160)" />
                <div class="rehab-my__actions">
                  <van-button size="small" round type="primary" color="var(--rehab-theme-color, #07c160)" @click.stop="continueLearning(item)">继续学习</van-button>
                </div>
                </div>
              </div>
            </div>
          </div>
        </van-pull-refresh>
      </div>

    <!-- VIP购买弹窗 -->
    <vip-purchase-popup 
      :show="showVipPopup" 
      :merchant="rehabMerchantState.queryMerchant || rehabMerchantConfig.key"
      @close="showVipPopup = false"
      @purchase="onVipPurchase"
    />
  </div>
</template>
<script>
import BaseTitleBarScrollPage from '@/h5/doucan/components/BaseTitleBarScrollPage.vue'
import VipPurchasePopup from '@/h5/doucan/components/VipPurchasePopup.vue'
import TicaoDao from '@/h5/doucan/network/Ticao.js'
import VipDao from '@/h5/doucan/network/VipDao.js'
import rehabMerchantMixin from './rehabMerchantMixin'
import { filterRehabCoursesByMerchant } from './merchantConfig'
import { listMerchantCourseHistory } from './merchantCourseSource'

export default {
  name: 'RehabCoursesMy',
  mixins: [rehabMerchantMixin],
  components: { 
    BaseTitleBarScrollPage,
    VipPurchasePopup
  },
  data () {
    return {
      keyword: '',
      myCourses: [],
      loading: false,
      refreshing: false,
      isApp: false,
      showVipPopup: false // VIP购买弹窗显示状态
    }
  },
  computed: {
    myTitle() {
      return `${this.rehabMerchantConfig.shortName || this.rehabMerchantConfig.name}·我的课程`
    },
    isLoggedIn() {
      return this.$store.state.user.profile.isLogin
    },
    filteredCourses () {
      const key = (this.keyword || '').trim().toLowerCase()
      if (!key) return this.myCourses
      return this.myCourses.filter(c => c.title.toLowerCase().includes(key))
    }
  },
  mounted () {
    this.$refs.container && this.$refs.container.stopLoading && this.$refs.container.stopLoading()
    
    // 获取 isApp 参数
    const params = new URLSearchParams(window.location.search)
    this.isApp = params.get('isApp') === '1' || params.get('isapp') === '1'
    this.initRehabMerchantFromRoute()
    this.applyRehabMerchantTheme()
    
    // 如果已登录，加载我的课程数据
    if (this.isLoggedIn) {
      this.loadMyCourses()
    }
  },
  watch: {
    // 监听登录状态变化，登录后自动加载课程
    isLoggedIn(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.loadMyCourses()
      }
    }
  },
  methods: {
    buildDetailRouteQuery(extraQuery = {}) {
      return this.buildRehabRouteQuery({
        isApp: this.isApp ? '1' : '0',
        ...extraQuery
      })
    },
    onSearch () {},
    goToLogin() {
      // 跳转到登录页面，登录成功后返回康复课程模块
      const query = this.rehabMerchantState.queryMerchant ? `&merchant=${encodeURIComponent(this.rehabMerchantState.queryMerchant)}` : ''
      this.$router.push(`/login/index?returnTo=rehab-courses${query}`)
    },
    async onRefresh() {
      console.log('开始刷新课程列表')
      try {
        // 重新加载课程数据，传递 isRefresh 参数
        await this.loadMyCourses(true)
        this.$toast.success('刷新成功')
      } catch (error) {
        console.error('刷新失败:', error)
        this.$toast.fail('刷新失败，请稍后重试')
      } finally {
        // 结束刷新状态
        this.refreshing = false
      }
    },
    async manualRefresh() {
      if (this.loading || this.refreshing) {
        return
      }
      
      console.log('手动刷新课程列表')
      this.refreshing = true
      await this.onRefresh()
    },
    async goDetail(item) {
      // VIP 课程需登录并校验会员
      if (item.vip || item.vipOnly) {
        if (!this.checkLogin()) {
          this.$toast('该课程为 VIP 专享课程，请先登录')
          this.$router.push('/login/index?returnTo=rehab-courses')
          return
        }
        const userId = this.$store.state.user.profile.userId
        const ok = await VipDao.isActiveVip(userId)
        if (!ok) {
          // 显示VIP购买弹窗
          this.showVipPopup = true
          return
        }
      }
      // 传参到详情页
      this.$router.push({
        name: 'RehabCourseDetail',
        query: this.buildDetailRouteQuery({
          id: item.id || item.courseId
        })
      })
    },
    continueLearning(item) {
      this.goDetail(item)
    },
    checkLogin() {
      return this.$store.state.user.profile.isLogin
    },
    formatWatchTime(watchTime) {
      if (!watchTime) {
        return '暂无记录'
      }
      
      try {
        const date = new Date(this.normalizeTimestamp(watchTime))
        const now = new Date()
        
        // 判断是否为今天
        const isToday = date.toDateString() === now.toDateString()
        if (isToday) {
          return `今天 ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
        }
        
        // 判断是否为昨天
        const yesterday = new Date(now)
        yesterday.setDate(yesterday.getDate() - 1)
        const isYesterday = date.toDateString() === yesterday.toDateString()
        if (isYesterday) {
          return `昨天 ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
        }
        
        // 统一格式：年月日 时分
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        
        return `${year}年${month}月${day}日 ${hours}:${minutes}`
      } catch (error) {
        console.error('日期格式化失败:', error)
        return '格式错误'
      }
    },
    // 兼容将时间戳统一标准化为毫秒数（支持字符串/秒/毫秒）
    normalizeTimestamp (val) {
      const num = Number(val)
      if (!num || isNaN(num)) return 0
      // 小于 1e12 视为秒级时间戳，乘以 1000
      return num < 1e12 ? num * 1000 : num
    },
    async loadMyCourses(isRefresh = false) {
      if (!this.isLoggedIn) {
        return
      }
      
      try {
        // 如果不是刷新操作，则显示加载状态
        if (!isRefresh) {
          this.loading = true
        }
        const userId = this.$store.state.user.profile.userId
        console.log('加载我的课程, userId:', userId)
        
        const data = this.useMarkdownMerchantCourses
          ? listMerchantCourseHistory(this.rehabMerchantConfig)
          : await TicaoDao.listAppletMyCourse(userId)
        const merchantCourses = filterRehabCoursesByMerchant(data, this.rehabMerchantConfig)
        console.log('我的课程数据:', merchantCourses)
        
        if (merchantCourses && Array.isArray(merchantCourses)) {
          // 按课程ID去重，保留最新的 lastWatchTime（后端返回字段为 id）
          const courseMap = new Map()
          merchantCourses.forEach(item => {
            const courseId = item.id
            const lastWatchTs = this.normalizeTimestamp(item.lastWatchTime)
            const existed = courseMap.get(courseId)
            if (!existed || lastWatchTs > this.normalizeTimestamp(existed.lastWatchTime)) {
              courseMap.set(courseId, item)
            }
          })

          // 数据映射，确保字段格式正确并兜底
          this.myCourses = Array.from(courseMap.values()).map(item => ({
            ...item,
            id: item.id,
            // 封面优先 coverUrl，兜底 detailUrl，再兜底占位图
            cover: item.coverUrl || item.detailUrl || item.cover || 'https://cdn.keihong.tech/img/202401-card-2.png',
            title: item.title || '未知课程',
            duration: item.duration || '待定',
            category: item.type || '康复训练',
            progress: Number(item.progress) || 0,
            vipOnly: !!item.vip,
            isFree: !item.vip,
            // 统一为毫秒时间戳，便于排序与展示
            lastWatchTime: this.normalizeTimestamp(item.lastWatchTime)
          }))

          // 按最后播放时间倒序排列
          this.myCourses.sort((a, b) => (b.lastWatchTime || 0) - (a.lastWatchTime || 0))
        } else {
          console.warn('我的课程数据格式异常:', data)
          this.myCourses = []
        }
      } catch (error) {
        console.error('加载我的课程失败:', error)
        if (!isRefresh) {
          this.$toast('加载课程失败，请稍后重试')
        }
        this.myCourses = []
      } finally {
        // 只在非刷新模式下重置 loading 状态
        if (!isRefresh) {
          this.loading = false
        }
      }
    },
    // VIP购买处理
    onVipPurchase(plan) {
      console.log('用户选择购买VIP套餐:', plan)
      // 这里可以跳转到支付页面或调用支付接口
      this.$toast(`对接中....正在跳转到${plan.name}购买页面...`)
      // TODO: 实现具体的购买逻辑
      // this.$router.push(`/vip/purchase?planId=${plan.id}`)
    },
    // 跳转到课程中心
    goToCourseCenter() {
      console.log('跳转到课程中心')
      this.$router.push({
        name: 'RehabCoursesHome',
        query: this.buildDetailRouteQuery()
      })
    },
    // 清空搜索
    clearSearch() {
      this.keyword = ''
    }
  }
}
</script>
<style scoped>
.rehab-title { 
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  text-align: center; 
  font-size: 18px; 
  font-weight: 600; 
  padding: 10px 0; 
  background: linear-gradient(135deg, var(--rehab-theme-color, #07c160) 0%, var(--rehab-theme-color-dark, #06a84e) 100%); 
  color: #fff; 
}

.rehab-title__refresh {
  position: absolute;
  right: 16px;
  font-size: 20px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.rehab-title__refresh:active {
  transform: scale(0.9);
}

.rehab-title__refresh--loading {
  animation: refresh-rotate 1s linear infinite;
}

@keyframes refresh-rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 登录引导样式 */
.rehab-my__login-guide {
  padding: 40px 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.rehab-my__login-card {
  background: #fff;
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  max-width: 320px;
  width: 100%;
}

.rehab-my__login-icon {
  margin-bottom: 16px;
}

.rehab-my__login-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.rehab-my__login-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 24px;
}

.rehab-my__login-actions {
  margin-top: 16px;
}

/* 已登录状态样式 */
.rehab-my__search { position: sticky; top: 0; z-index: 20; background: var(--rehab-theme-color, #07c160); padding: 8px 12px; }
.rehab-my__search ::v-deep .van-search { background: transparent !important; padding: 0 !important; }
.rehab-my__search ::v-deep .van-search__content { background: #fff; }
.rehab-my { padding: 0 12px 12px;margin-top: 12px; }

/* 加载状态 */
.rehab-my__loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 12px;
}

/* 空状态 */
.rehab-my__empty { 
  background: #fff; 
  border-radius: 12px; 
  margin-bottom: 12px;
  overflow: hidden;
}

/* 无学习记录状态 */
.rehab-my__empty-container {
  padding: 48px 24px 32px;
  text-align: center;
}

.rehab-my__empty-icon {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.rehab-my__empty-title {
  font-size: 18px;
  font-weight: 600;
  color: #323233;
  margin-bottom: 16px;
}

.rehab-my__empty-desc {
  color: #646566;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 24px;
}

.rehab-my__empty-desc p {
  margin: 0 0 8px 0;
  padding: 0;
}

.rehab-my__empty-desc p:last-child {
  margin-bottom: 0;
}

.rehab-my__empty-actions {
  margin-bottom: 28px;
}

.rehab-my__empty-tips {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
  padding: 0 16px;
}

.rehab-my__tip-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #646566;
  justify-content: center;
}

.rehab-my__tip-item span {
  font-weight: 500;
}

.rehab-my__empty-refresh {
  font-size: 12px;
  color: #07c160;
  opacity: 0.7;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f2f3f5;
}

/* 搜索无结果状态 */
.rehab-my__empty-search {
  padding: 40px 24px;
  text-align: center;
  color: #999;
}

.rehab-my__empty-search .rehab-my__empty-icon {
  margin-bottom: 16px;
}

.rehab-my__empty-search .rehab-my__empty-title {
  font-size: 16px;
  font-weight: 500;
  color: #646566;
  margin-bottom: 8px;
}

.rehab-my__empty-search .rehab-my__empty-desc {
  font-size: 13px;
  color: #969799;
  margin-bottom: 20px;
}

.rehab-my__empty-search .rehab-my__empty-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 0;
}
.rehab-my__list { display: grid; grid-template-columns: 1fr; gap: 12px; }
.rehab-my__card { 
  display: flex; 
  background: #fff; 
  border-radius: 12px; 
  overflow: hidden; 
  box-shadow: 0 2px 8px rgba(0,0,0,0.06); 
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rehab-my__card:active {
  transform: scale(0.98);
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
}
.rehab-my__cover { width: 120px; height: 100%; min-height: 92px; background-size: cover; background-position: center; position: relative; }
.rehab-my__tag { position: absolute; left: 8px; top: 8px; color: #fff; font-size: 12px; padding: 2px 8px; border-radius: 12px; }
.rehab-my__tag--vip { background: linear-gradient(135deg, #ff8f3f 0%, #ff5b2e 100%); }
.rehab-my__tag--free { background: linear-gradient(135deg, #34d399 0%, #10b981 100%); }
.rehab-my__info { flex: 1; padding: 10px 12px; display: flex; flex-direction: column; gap: 6px; }
.rehab-my__title { font-size: 15px; font-weight: 600; }
.rehab-my__meta { font-size: 12px; color: #999; }
.rehab-my__watch-time { font-size: 11px; color: var(--rehab-theme-color, #07c160); margin-top: 2px; }
.rehab-my__actions { text-align: right; }
</style>


